import type { EnvironmentKey } from '@web-config-app/core';
import type { MultiEnvironmentActionConfirmationModal } from '../../types/multi-environment.types';

type FormatMessage = (
  descriptor: { id: string },
  values?: Record<string, string | number>,
) => string;

/** All fields describe the *target* environment of the copy. */
interface GetCopyToEnvironmentModalPropsParams {
  entityName: string;
  environmentName: string;
  publishDangerously?: boolean;
  environmentKey: EnvironmentKey;
  formatMessage: FormatMessage;
}

/**
 * Builds the confirmation modal shown before "Copy to {environment}" runs, or
 * `undefined` when the target is not dangerous.
 *
 * Both halves of the predicate are needed. A tenant can raise
 * `publishDangerously` on a non-production environment, so a production-only
 * test would skip friction that tenant asked for; and nothing validates that a
 * production registry entry carries the flag, so a flag-only test could miss a
 * production target.
 */
export const getCopyToEnvironmentModalProps = ({
  entityName,
  environmentName,
  publishDangerously,
  environmentKey,
  formatMessage,
}: GetCopyToEnvironmentModalPropsParams):
  | MultiEnvironmentActionConfirmationModal
  | undefined => {
  if (!publishDangerously && environmentKey !== 'production') {
    return undefined;
  }

  return {
    title: formatMessage(
      { id: 'COPY_TO_ENVIRONMENT_DANGEROUSLY_MODAL_TITLE' },
      { environment: environmentName },
    ),
    description: formatMessage(
      { id: 'COPY_TO_ENVIRONMENT_DANGEROUSLY_MODAL_DESCRIPTION' },
      { entity: entityName, environment: environmentName },
    ),
    confirmCta: formatMessage(
      { id: 'COPY_TO_ENVIRONMENT_DANGEROUSLY_MODAL_PRIMARY_CTA' },
      { environment: environmentName },
    ),
    checkboxConfirmationLabel: formatMessage(
      { id: 'COPY_TO_ENVIRONMENT_DANGEROUSLY_MODAL_CHECKBOX' },
      { entity: entityName, environment: environmentName },
    ),
  };
};
